import React, { useState } from 'react';
import toast from 'react-hot-toast';
import {
  useLLMSettings,
  useUpdateLLMKey,
  useDeleteLLMKey,
} from '../../../../api/hooks/settings.hook';

const formatDateTime = (value?: string | null) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  return date.toLocaleString();
};

type InfoTileProps = {
  label: string;
  value: string;
};

const InfoTile: React.FC<InfoTileProps> = ({ label, value }) => (
  <div className="rounded-lg border border-neutral-200 p-3 sm:p-4 bg-neutral-50">
    <p className="text-[10px] sm:text-xs uppercase tracking-wide text-neutral-500">{label}</p>
    <p className="text-xs sm:text-sm font-medium text-neutral-900 break-all mt-0.5">{value}</p>
  </div>
);

export const LLMConfiguration = () => {
  const llmSettings = useLLMSettings();
  const updateKey = useUpdateLLMKey();
  const deleteKey = useDeleteLLMKey();
  const [apiKey, setApiKey] = useState('');

  const loading = llmSettings.isLoading;
  const hasKey = llmSettings.data?.has_api_key ?? false;
  const source = llmSettings.data?.source ?? 'missing';
  const updatedAt = llmSettings.data?.updated_at;

  const handleSave = async () => {
    if (!apiKey.trim()) {
      toast.error('Enter an API key before saving.');
      return;
    }
    try {
      await updateKey.mutateAsync(apiKey.trim());
      setApiKey('');
      toast.success('API key saved.');
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unable to save the API key.';
      toast.error(message);
    }
  };

  const handleDelete = async () => {
    try {
      await deleteKey.mutateAsync();
      toast.success('Removed stored API key.');
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unable to remove the API key.';
      toast.error(message);
    }
  };

  return (
    <div className="flex-1 overflow-y-auto">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 sm:py-8 space-y-4 sm:space-y-6">
        <div className="space-y-1">
          <h1 className="text-xl sm:text-2xl font-semibold text-neutral-900">
            LLM API Configuration
          </h1>
          <p className="text-xs sm:text-sm text-neutral-600">
            Store workspace-specific credentials for the default language model provider.
          </p>
        </div>
        <section className="bg-white rounded-xl shadow-sm border border-neutral-200 p-4 sm:p-6 space-y-4">
          {loading ? (
            <div className="text-neutral-500 text-sm">Loading LLM settings…</div>
          ) : (
            <div className="space-y-4">
              <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
                <InfoTile label="Provider" value={llmSettings.data?.settings.provider ?? '—'} />
                <InfoTile label="Model" value={llmSettings.data?.settings.model ?? '—'} />
                <InfoTile label="API key source" value={source} />
                <InfoTile label="Last updated" value={formatDateTime(updatedAt)} />
              </div>
              <div className="space-y-2">
                <label className="block text-sm font-medium text-neutral-700">API key</label>
                <input
                  type="password"
                  placeholder={hasKey ? '••••••••' : 'Enter API key'}
                  value={apiKey}
                  onChange={(event) => setApiKey(event.target.value)}
                  className="w-full rounded-md border border-neutral-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-neutral-800"
                />
                <div className="flex gap-3">
                  <button
                    type="button"
                    className="inline-flex items-center rounded-md bg-neutral-900 px-4 py-2 text-sm font-medium text-white hover:bg-neutral-800 focus:outline-none focus:ring-2 focus:ring-neutral-500"
                    onClick={handleSave}
                    disabled={updateKey.isLoading}
                  >
                    {updateKey.isLoading ? 'Saving…' : hasKey ? 'Update key' : 'Save key'}
                  </button>
                  <button
                    type="button"
                    className="inline-flex items-center rounded-md border border-neutral-300 px-4 py-2 text-sm font-medium text-neutral-700 hover:bg-neutral-100 focus:outline-none focus:ring-2 focus:ring-neutral-500"
                    onClick={handleDelete}
                    disabled={!hasKey || deleteKey.isLoading}
                  >
                    {deleteKey.isLoading ? 'Removing…' : 'Remove stored key'}
                  </button>
                </div>
              </div>
            </div>
          )}
        </section>
      </div>
    </div>
  );
};
